import React from 'react';

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
}

interface Requirement {
  regex: RegExp;
  text: string;
  met: boolean;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showRequirements = true
}) => {
  // Handle null/undefined password
  const safePassword = password || '';
  
  const requirements: Requirement[] = [
    {
      regex: /.{8,}/,
      text: 'Pelo menos 8 caracteres',
      met: safePassword.length >= 8
    },
    {
      regex: /[A-Z]/,
      text: 'Pelo menos 1 letra maiúscula',
      met: /[A-Z]/.test(safePassword)
    },
    {
      regex: /[a-z]/,
      text: 'Pelo menos 1 letra minúscula',
      met: /[a-z]/.test(safePassword)
    },
    {
      regex: /[0-9]/,
      text: 'Pelo menos 1 número',
      met: /[0-9]/.test(safePassword)
    },
    {
      regex: /[@#!$%&*]/,
      text: 'Pelo menos 1 carácter especial (@, #, !, $, %, &, *)',
      met: /[@#!$%&*]/.test(safePassword)
    }
  ];

  const calculateStrength = (): { score: number; label: string; color: string } => {
    const metRequirements = requirements.filter(req => req.met).length;
    
    if (safePassword.length === 0) {
      return { score: 0, label: '', color: '#e5e7eb' };
    }
    
    if (metRequirements <= 2) {
      return { score: 25, label: 'Fraca', color: '#ef4444' };
    }
    
    if (metRequirements <= 3) {
      return { score: 50, label: 'Média', color: '#f59e0b' };
    }
    
    if (metRequirements <= 4) {
      return { score: 75, label: 'Forte', color: '#10b981' };
    }
    
    return { score: 100, label: 'Muito Forte', color: '#059669' };
  };

  const strength = calculateStrength();

  return (
    <div className="space-y-2">
      {/* Barra de força */}
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">Força da palavra-passe</span>
          {safePassword.length > 0 && (
            <span 
              className="text-sm font-medium"
              style={{ color: strength.color }}
            >
              {strength.label}
            </span>
          )}
        </div>
        <div
          className="w-full bg-gray-200 rounded-full h-2"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={strength.score}
          aria-label={`Força da palavra-passe: ${strength.score}%`}
        >
          <div
            className="h-2 rounded-full transition-all duration-300 ease-in-out"
            style={{
              width: `${strength.score}%`,
              backgroundColor: strength.color
            }}
          />
        </div>
      </div>

      {/* Lista de requisitos */}
      {showRequirements && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-700 mb-2">
            Requisitos da palavra-passe:
          </p>
          <div className="space-y-1">
            {requirements.map((requirement, index) => (
              <div 
                key={index} 
                className="flex items-center space-x-2 text-sm"
              >
                <span
                  className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    requirement.met 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {requirement.met ? '✓' : '○'}
                </span>
                <span 
                  className={
                    requirement.met 
                      ? 'text-green-700' 
                      : 'text-gray-600'
                  }
                >
                  {requirement.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;